<script setup lang="ts">
type color = {
  label: string
  swatch: string
}

type colorVariant = {
  url: string
  color: [color]
  stockStatus?: boolean
}

export interface Props {
  product: EsProduct
  colorVariants?: colorVariant[] | null
  limit?: number
}

const fakeColors: colorVariant[] = [
  {
    url: '#',
    color: [{
      label: 'Bruin',
      swatch: '#605149',
    }],
    stockStatus: true,
  },
  {
    url: '#',
    color: [{
      label: 'Zwart',
      swatch: '#1A3940',
    }],
    stockStatus: true,
  },
  {
    url: '#',
    color: [{
      label: 'Beige',
      swatch: '#E9E1D9',
    }],
    stockStatus: true,
  },
  ...(new Array(37 + 1)), // + 1 to account for current color
]

const props = withDefaults(defineProps<Props>(), {
  limit: 3,
})

const { public: config } = useRuntimeConfig()
const swatch = (swatch: string | undefined | null) => {
  if (!swatch) return ''
  const swatchPath = config.baseDomain + '/media/attribute/swatch'
  return swatch.startsWith('#') ? `background-color: ${swatch}` : `background-image: url(${swatchPath + swatch}); border: 0;`
}

const currentColor = computed(() => {
  return props.product?.color?.[0] || fakeColors[0].color[0]
})

const colorVariants = computed(() => {
  if (props.colorVariants?.length) {
    return props.colorVariants
  }

  if (props?.product?.color?.length) {
    return [
      ...(props.product?.color?.map((color: colorVariant) => {
        return {
          url: props.product.url,
          color: [color],
          stockStatus: true,
        }
      }) || []),
      ...fakeColors,
    ].slice(1, props.limit) // 1 to skip current color
  }

  return fakeColors.slice(1, props.limit) // 1 to skip current color
})

const limitedColorVariants = computed(() =>
  colorVariants.value.slice(0, colorVariants.value.length > props.limit ? props.limit - 1 : colorVariants.value.length),
)
</script>

<template>
  <div class="color-variant">
    <div
      class="color-variant__swatch current in-stock"
      :style="swatch(currentColor.swatch)"
    />

    <div
      v-for="(colorVariant, index) in limitedColorVariants"
      :key="index"
      class="color-variant__swatch"
      :class="[
        colorVariant.stockStatus ? 'in-stock' : 'out-of-stock',
      ]"
      :style="swatch(colorVariant?.color?.[0]?.swatch)"
    />

    <span class="color-variant__more">
      +
      <template v-if="colorVariants.length > limitedColorVariants.length">
        {{ colorVariants.length - limitedColorVariants.length }}
      </template>
      <template v-else>
        40
      </template>
    </span>
  </div>
</template>

<style>
  @import url("~layers/app/components/Product/ColorVariant.css");
</style>
